import { SubgraphIsland, useIsland } from 'hooks/useIsland'
import { SubgraphV2Pool, useV2Pool } from 'hooks/useV2Pools'
import { useMemo } from 'react'
import { IslandApiResponse } from 'state/data/slice'

export interface UsePoolResult {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  pool: IslandApiResponse | undefined
  type: PoolType
  error: any
  refetch: () => void
}

export type SubgraphPool = SubgraphIsland | SubgraphV2Pool

export type PoolType = 'Generic' | 'V2' | 'Island' | null

function transformIslandToApiResponse(island: SubgraphIsland, nativePrice: number | null): IslandApiResponse {
  return {
    id: island.id,
    provider: 'kodiak',
    lowerTick: parseFloat(island.lowerTick) || 0,
    upperTick: parseFloat(island.upperTick) || 0,
    currentTick: parseFloat(island.pool?.tick) || 0,
    tvl: parseFloat(island.totalValueLockedUSD || '0'),
    farmTvl: 0,
    apr: island.apr.averageApr || 0,
    weeklyFeesEarnedUSD: parseFloat(island.weeklyFeesEarnedUSD || '0'),
    feeTier: island.pool?.feeTier || 3000,
    totalApr: island.apr.averageApr || 0,
    lastUpdated: new Date().toISOString(),
    isSweetened: false,
    poolAddress: island.pool?.id,
    isRewardVault: false,
    isVolatile: false,
    token0: {
      id: island.token0?.id || '',
      symbol: island.token0?.symbol || '',
      name: island.token0?.name || '',
      decimals: island.token0?.decimals || 18,
      price: nativePrice !== null ? parseFloat(island.token0?.derivedETH || '0') * nativePrice : 0,
    },
    token1: {
      id: island.token1?.id || '',
      symbol: island.token1?.symbol || '',
      name: island.token1?.name || '',
      decimals: island.token1?.decimals || 18,
      price: nativePrice !== null ? parseFloat(island.token1?.derivedETH || '0') * nativePrice : 0,
    },
    pool: {
      id: island.pool?.id ?? '',
      protocol: 'island',
      fee: island.pool?.feeTier,
      token0Id: island.token0.id,
      token1Id: island.token1.id,
      token0Price: null,
      token1Price: null,
      totalValueLockedETH: null,
      totalValueLockedUSD: null,
      totalValueLockedToken0: null,
      totalValueLockedToken1: null,
    },
    farm: null,
    tokenLp: {
      id: island.id || '',
      symbol: island.symbol || '',
      name: island.name || '',
      decimals: island?.outputToken?.decimals || 18,
      price: nativePrice !== null ? parseFloat(island?.outputToken?.derivedETH || '0') * nativePrice : 0,
      totalSupply: parseFloat(island?.outputToken?.totalSupply || '0'),
      priceStrategy: 'default',
    },
    balanceUSD: 0,
    farmBalanceUSD: 0,
    vaultBalanceUSD: 0,
    baultBalanceUSD: 0,
    externalBalanceUSD: 0,
    baults: [],
  }
}

// Transform SubgraphV2Pool to IslandApiResponse format
function transformV2PoolToApiResponse(pool: SubgraphV2Pool, nativePrice: number | null): IslandApiResponse {
  return {
    id: pool.id,
    provider: 'v2',
    lowerTick: 0, // V2 pools don't have ticks
    upperTick: 0,
    currentTick: 0,
    tvl: parseFloat(pool.totalValueLockedUSD || '0'),
    farmTvl: 0,
    apr: parseFloat(pool.apr.averageApr || '0'),
    weeklyFeesEarnedUSD: parseFloat(pool.weeklyFeesEarnedUSD || '0'),
    feeTier: 3000, // Default V2 fee tier
    totalApr: parseFloat(pool.apr.averageApr || '0'),
    lastUpdated: new Date().toISOString(),
    isSweetened: false,
    isRewardVault: false,
    isVolatile: false,
    poolAddress: pool.id,
    token0: {
      id: pool.token0?.id || '',
      symbol: pool.token0?.symbol || '',
      name: pool.token0?.name || '',
      decimals: pool.token0?.decimals || 18,
      price: nativePrice !== null ? parseFloat(pool.token0?.derivedETH || '0') * nativePrice : 0,
    },
    token1: {
      id: pool.token1?.id || '',
      symbol: pool.token1?.symbol || '',
      name: pool.token1?.name || '',
      decimals: pool.token1?.decimals || 18,
      price: nativePrice !== null ? parseFloat(pool.token1?.derivedETH || '0') * nativePrice : 0,
    },
    pool: {
      id: pool.id,
      protocol: 'v2',
      fee: 3000,
      token0Id: pool.token0.id,
      token1Id: pool.token1.id,
      token0Price: null,
      token1Price: null,
      totalValueLockedETH: null,
      totalValueLockedUSD: null,
      totalValueLockedToken0: null,
      totalValueLockedToken1: null,
    },
    farm: null,
    tokenLp: {
      id: pool.id || '',
      symbol: pool.symbol || '',
      name: pool.name || '',
      decimals: 18,
      // price: nativePrice !== null ? parseFloat(pool.tokenLp.price || '0') * nativePrice : 0,
      price: 0,
      totalSupply: parseFloat(pool.totalSupply || '0'),
      priceStrategy: 'default',
    },

    userPositions: [],
    baults: [],
    balanceUSD: 0,
    farmBalanceUSD: 0,
    vaultBalanceUSD: 0,
    baultBalanceUSD: 0,
    externalBalanceUSD: 0,
  }
}

export function usePool(poolAddress: string | undefined, nativePrice: number | null): UsePoolResult {
  const islandResult = useIsland(poolAddress)
  const v2Result = useV2Pool(poolAddress)

  return useMemo(() => {
    if (!islandResult.island && !v2Result.pool && (islandResult.isLoading || v2Result.isLoading)) {
      return {
        isLoading: true,
        isFetching: false,
        isUninitialized: false,
        isError: false,
        pool: undefined,
        type: null,
        error: null,
        refetch: () => {
          islandResult.refetch()
          v2Result.refetch()
        },
      }
    }

    // If both queries are uninitialized (no poolAddress provided)
    if (islandResult.isUninitialized && v2Result.isUninitialized) {
      return {
        isLoading: false,
        isFetching: false,
        isUninitialized: true,
        isError: false,
        pool: undefined,
        type: null,
        error: null,
        refetch: () => {
          islandResult.refetch()
          v2Result.refetch()
        },
      }
    }

    // Check if we found an island pool
    if (islandResult.island) {
      return {
        isLoading: false,
        isFetching: islandResult.isFetching,
        isUninitialized: false,
        isError: false,
        pool: transformIslandToApiResponse(islandResult.island, nativePrice),
        type: 'Island' as const,
        error: null,
        refetch: islandResult.refetch,
      }
    }

    // Check if we found a V2 pool
    if (v2Result.pool) {
      return {
        isLoading: false,
        isFetching: v2Result.isFetching,
        isUninitialized: false,
        isError: false,
        pool: transformV2PoolToApiResponse(v2Result.pool, nativePrice),
        type: 'V2' as const,
        error: null,
        refetch: v2Result.refetch,
      }
    }

    // If neither pool was found but there were errors
    if (islandResult.isError || v2Result.isError) {
      return {
        isLoading: false,
        isFetching: false,
        isUninitialized: false,
        isError: true,
        pool: undefined,
        type: null,
        error: islandResult.error || v2Result.error,
        refetch: () => {
          islandResult.refetch()
          v2Result.refetch()
        },
      }
    }

    // No pool found and no errors
    return {
      isLoading: false,
      isFetching: false,
      isUninitialized: false,
      isError: false,
      pool: undefined,
      type: 'Generic',
      error: null,
      refetch: () => {
        islandResult.refetch()
        v2Result.refetch()
      },
    }
  }, [islandResult, v2Result])
}
